import { Box, Button, Flex, Grid, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FormikCheckbox } from 'components/Formik';
import Loader from 'components/Loader';
import { routePaths } from 'global/routePaths';
import { useDisconnectPaycor } from 'hooks/integrations/paycor/useDisconnectPaycor';
import { useFetchPaycorSettings } from 'hooks/integrations/paycor/useFetchPaycorSettings';
import { useSavePaycorSettings } from 'hooks/integrations/paycor/useSavePaycorSettings';
import { useCompanyInfo } from 'store/useUserStore';

import { initialPaycorSettingsData } from './schema';

const Settings: React.FC<unknown> = () => {
  const [formData, setFormData] = useState<any>(initialPaycorSettingsData);

  const { status: savePaycorSettingsStatus, mutateAsync: savePaycorSettings } = useSavePaycorSettings();
  const { status: paycorDisconnectStatus, mutateAsync: disconnectPaycorAsync } = useDisconnectPaycor();

  const { t } = useTranslation(['common', 'integrations']);

  const navigate = useNavigate();
  const companyInfo = useCompanyInfo();

  const { isFetching: isFetchingSettings, refetch } = useFetchPaycorSettings({
    onSettled: (data: any) => {
      if (data) {
        const settingData = data?.payload;

        const pFormData = {
          export_paygrids: settingData?.export_paygrids ? ['1'] : [],
          // use_labor_codes: settingData?.use_labor_codes === 1 ? ['1'] : [],
        };

        setFormData(pFormData);
      }
    },
  });

  const onConfigureMapping = () => {
    navigate(routePaths.integrations.paycor.mapping);
  };

  const handleDisconnectPaycor = () => {
    disconnectPaycorAsync().then(() => {
      navigate(routePaths.integrations.paycor.base, { replace: true });
    });
  };

  const handleFormSubmit = async (values: typeof initialPaycorSettingsData, { resetForm }: any) => {
    const postData = {
      export_paygrids: values.export_paygrids.length > 0 ? true : false,
      // use_labor_codes: values.export_paygrids.length > 0 && values.use_labor_codes.length > 0 ? true : false,
    };

    savePaycorSettings(postData).then(() => {
      resetForm();
      refetch();
    });
  };

  return (
    <Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <Flex flexDirection="column">
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px" mb={6}>
            <Text fontSize="sm" color="gray.500" mb={4}>
              {companyInfo?.name}
            </Text>

            <Button
              display="block"
              variant="link"
              mb={4}
              color="primary.300"
              cursor="pointer"
              onClick={onConfigureMapping}
            >
              {t('integrations:configureMappings')}
            </Button>

            <Button
              variant="link"
              size="sm"
              color="primary.300"
              isLoading={paycorDisconnectStatus === 'loading'}
              onClick={handleDisconnectPaycor}
            >
              {t('integrations:generalDisconnect')}
            </Button>
          </Box>
        </Flex>
        <Box>
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px">
            <Text fontSize="md" mb={4} fontWeight="semibold">
              {t('integrations:paycor.settings')}
            </Text>
            {isFetchingSettings ? (
              <Loader height="150px" />
            ) : (
              <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
                {() => (
                  <Form noValidate>
                    <FormikCheckbox
                      options={[{ label: 'Send Time & Mileage to Paycor Paygrid', value: '1' }]}
                      name="export_paygrids"
                    />

                    {/* {Array.isArray(values.export_paygrids) && values.export_paygrids.includes('1') ? (
                      <FormikCheckbox options={[{ label: 'Use Labor Codes', value: '1' }]} name="use_labor_codes" />
                    ) : null} */}

                    <Button
                      type="submit"
                      colorScheme="secondary"
                      isLoading={savePaycorSettingsStatus === 'loading'}
                      minWidth="100px"
                    >
                      {t('common:save')}
                    </Button>
                  </Form>
                )}
              </Formik>
            )}
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};

export default Settings;
